import React, { useState, useCallback, useEffect } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { apiFetch, interactiveFetch, getStoredToken } from "../api/index.js";
import { StandardPageLayout, Button as CommonButton, LoadingSpinner } from "../components/Common.jsx";
import { ShieldCheck, Search, TrendingUp, AlertTriangle, CheckCircle2, Clock, Activity, Database, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDateTime } from '../utils/dateTime';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ─── Anomaly Badge ───────────────────────────────────────────────────────────
function AnomalyBadge({ flags }) {
  if (!flags || Object.keys(flags).length === 0) return null;
  const names = Object.keys(flags).filter((k) => flags[k]);
  const colors = {
    HIGH_NULL:         "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
    HIGH_ZERO_RATE:    "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300",
    HIGH_OUTLIER_RATE: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300",
    HIGH_CARDINALITY:  "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300",
    LOW_CARDINALITY:   "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400",
  };
  return (
    <div className="flex flex-wrap gap-1">
      {names.map((n) => (
        <span key={n} className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${colors[n] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
          {n.replace(/_/g, " ")}
        </span>
      ))}
    </div>
  );
}

// ─── Drift Badge ─────────────────────────────────────────────────────────────
function DriftBadge({ level }) {
  if (!level) return <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>;
  const style = level === "High"
    ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300"
    : "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300";
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${style}`}>
      {level === "High" ? "🔴" : "⚠"} {level}
    </span>
  );
}

// ─── Health Score Ring ────────────────────────────────────────────────────────
function HealthRing({ score }) {
  const color = score >= 80 ? "#16a34a" : score >= 60 ? "#d97706" : "#dc2626";
  const r = 40; const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="flex flex-col items-center">
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#e5e7eb" strokeWidth="10" className="dark:stroke-slate-700" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform="rotate(-90 50 50)" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
        <text x="50" y="46" textAnchor="middle" fill={color} fontSize="18" fontWeight="bold">{score}</text>
        <text x="50" y="62" textAnchor="middle" fill="#9ca3af" fontSize="9">DQ SCORE</text>
      </svg>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color = "text-blue-600", bg = "bg-blue-50 dark:bg-blue-900/20" }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-gray-100 dark:border-slate-800 shadow-sm flex items-center gap-4">
      <div className={`${bg} p-3 rounded-xl`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-xl font-bold text-gray-900 dark:text-slate-100">{value ?? "—"}</p>
      </div>
    </div>
  );
}

// ─── Column Detail Slide-out ──────────────────────────────────────────────────
function ColumnDetailPanel({ col, onClose }) {
  if (!col) return null;
  const isNumeric = col.histogram && col.histogram.length > 0;
  const chartData = isNumeric
    ? col.histogram.map((b) => ({ name: b.label, count: b.count }))
    : col.topValues ? Object.entries(col.topValues).map(([v, c]) => ({ name: v, count: c })) : [];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-[420px] z-50 bg-white dark:bg-slate-900 shadow-2xl border-l border-gray-200 dark:border-slate-700 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-800">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest">Column Detail</p>
            <p className="text-lg font-bold text-gray-900 dark:text-slate-100 mt-0.5">{col.name}</p>
            <p className="text-xs text-gray-400 mt-0.5">{col.type}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-xl transition-colors">✕</button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Null Rate", col.nullRate != null ? `${(col.nullRate * 100).toFixed(1)}%` : "—"],
              ["Unique Count", col.uniqueCount ?? "—"],
              ["Uniqueness", col.uniquenessRate != null ? `${(col.uniquenessRate * 100).toFixed(1)}%` : "—"],
              ["Top Value", col.topValue ?? "—"],
              ...(isNumeric ? [
                ["Mean", col.mean?.toFixed(3) ?? "—"],
                ["Median", col.median?.toFixed(3) ?? "—"],
                ["Std Dev", col.stdDev?.toFixed(3) ?? "—"],
                ["CV%", col.cvPct != null ? `${col.cvPct.toFixed(1)}%` : "—"],
                ["Zero Rate", col.zeroRate != null ? `${(col.zeroRate * 100).toFixed(1)}%` : "—"],
                ["Outliers", col.outlierCount ?? "—"],
              ] : [
                ["Min Length", col.minLength ?? "—"],
                ["Max Length", col.maxLength ?? "—"],
                ["Avg Length", col.avgLength?.toFixed(1) ?? "—"],
              ]),
            ].map(([lbl, val]) => (
              <div key={lbl} className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3">
                <p className="text-xs text-gray-400 dark:text-slate-500">{lbl}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-slate-100 mt-0.5 truncate">{val}</p>
              </div>
            ))}
          </div>

          {/* Sample values */}
          {col.sampleValues?.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-2">Sample Values</p>
              <div className="flex flex-wrap gap-1">
                {col.sampleValues.map((v, i) => (
                  <span key={i} className="px-2 py-1 rounded bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 text-xs">{v}</span>
                ))}
              </div>
            </div>
          )}

          {/* Chart */}
          {chartData.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-2">{isNumeric ? "Distribution" : "Top Values"}</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" className="dark:stroke-slate-700" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#9ca3af" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={`hsl(${210 + i * 18}, 70%, 55%)`} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Drift details */}
          {col.driftDetails?.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-2">Drift Details</p>
              <div className="space-y-2">
                {col.driftDetails.map((d, i) => (
                  <div key={i} className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 flex justify-between items-center border border-yellow-100 dark:border-yellow-800">
                    <span className="text-xs font-medium text-gray-700 dark:text-slate-300">{d.field}</span>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">{d.prev} → {d.curr}</p>
                      <p className="text-xs font-bold text-yellow-700 dark:text-yellow-400">Δ {d.delta} {d.deltaPct != null ? `(${d.deltaPct}%)` : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DqProfilerPage() {
  const [sourceId, setSourceId]       = useState("");
  const [tableName, setTableName]     = useState("");
  const [loading, setLoading]         = useState(false);
  const [profile, setProfile]         = useState(null);
  const [history, setHistory]         = useState([]);
  const [selectedCol, setSelectedCol] = useState(null);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState("columns");

  // Data Models dropdown
  const [dataModels, setDataModels]           = useState([]);
  const [modelsLoading, setModelsLoading]     = useState(false);
  const [selectedModel, setSelectedModel]     = useState(null); // full model object
  const [availableTables, setAvailableTables] = useState([]);

  // Fetch data models on mount
  useEffect(() => {
    setModelsLoading(true);
    const token = getStoredToken();
    apiFetch("/datamodels/", token)
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setDataModels(list);
      })
      .catch(e => console.warn("[DQ Profiler] Could not fetch data models:", e))
      .finally(() => setModelsLoading(false));
  }, []);

  // When a model is selected, extract its tables
  const handleModelSelect = useCallback((modelId) => {
    const model = dataModels.find(m => m.id === modelId);
    setSelectedModel(model || null);
    setSourceId(modelId);
    setTableName("");
    setProfile(null);
    setError(null);
    // Extract table names from model_definition
    const tables = model?.model_definition?.tables?.map(t => t.name || t.alias) ||
                   model?.tables?.map(t => t.name) || [];
    setAvailableTables(tables);
  }, [dataModels]);

  const runProfile = useCallback(async () => {
    if (!sourceId) return;
    setLoading(true); setError(null); setProfile(null); setSelectedCol(null);
    const token = getStoredToken();
    try {
      const data = await interactiveFetch("/dq/profile", token, {
        method: "POST",
        body: JSON.stringify({ source_id: sourceId, table_name: tableName || undefined }),
      });
      setProfile(data);
      try {
        const hist = await apiFetch(`/dq/dataprofile/profile/history?source_id=${encodeURIComponent(sourceId)}&limit=10`, token);
        setHistory(hist);
      } catch (_) {}
    } catch (e) {
      setError(e.message || "Failed to run profile.");
    } finally {
      setLoading(false);
    }
  }, [sourceId, tableName]);

  const anomalyCols = profile?.columns?.filter(c => c.anomalyFlags && Object.keys(c.anomalyFlags).length > 0) || [];
  const driftCols   = profile?.columns?.filter(c => c.driftLevel === "Medium" || c.driftLevel === "High") || [];

  // ── Export CSV ────────────────────────────────────────────────────────────
  const exportCSV = useCallback(() => {
    if (!profile?.columns) return;
    const headers = ["Column", "Type", "Null %", "Unique %", "Mean", "Std Dev", "CV%", "Anomalies", "Drift"];
    const rows = profile.columns.map(col => [
      col.name,
      col.type,
      col.nullRate != null ? `${(col.nullRate * 100).toFixed(1)}%` : "",
      col.uniquenessRate != null ? `${(col.uniquenessRate * 100).toFixed(1)}%` : "",
      col.mean?.toFixed(3) ?? "",
      col.stdDev?.toFixed(3) ?? "",
      col.cvPct != null ? `${col.cvPct.toFixed(1)}%` : "",
      col.anomalyFlags ? Object.keys(col.anomalyFlags).filter(k => col.anomalyFlags[k]).join(" | ") : "",
      col.driftLevel ?? "",
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `dq_profile_${selectedModel?.name || sourceId}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [profile, selectedModel, sourceId]);

  // ── Export PDF (browser print) ────────────────────────────────────────────
  const exportPDF = useCallback(() => {
    if (!profile) return;
    const score = Math.round(profile.dqScore ?? 0);
    const scoreColor = score >= 80 ? "#16a34a" : score >= 60 ? "#d97706" : "#dc2626";
    const win = window.open("", "_blank");
    win.document.write(`
      <!DOCTYPE html><html><head><title>DQ Profile Report</title>
      <style>
        *{box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;padding:32px;color:#111;max-width:1100px;margin:0 auto}
        h1{font-size:26px;font-weight:700;margin:0 0 4px}p.sub{color:#666;font-size:13px;margin:0 0 24px}
        .stats{display:flex;gap:16px;flex-wrap:wrap;margin-bottom:28px}
        .stat{background:#f8faff;border:1px solid #e0e7ff;padding:14px 22px;border-radius:10px;min-width:120px}
        .stat-label{font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#6b7280;margin-bottom:4px}
        .stat-value{font-size:24px;font-weight:700;color:#111}
        .ring{width:80px;height:80px;position:relative;display:inline-flex;align-items:center;justify-content:center}
        h2{font-size:15px;font-weight:600;margin:24px 0 10px;border-bottom:1px solid #e5e7eb;padding-bottom:6px}
        table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:24px}
        th{background:#f0f4ff;text-align:left;padding:8px 10px;border-bottom:2px solid #c7d2fe;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#4b5563}
        td{padding:7px 10px;border-bottom:1px solid #f3f4f6;vertical-align:middle}
        tr:hover td{background:#fafafa}.badge{background:#fef2f2;color:#b91c1c;padding:2px 6px;border-radius:4px;font-size:9px;font-weight:700;margin:1px;display:inline-block}
        .score-good{color:#16a34a}.score-ok{color:#d97706}.score-bad{color:#dc2626}
        footer{margin-top:32px;font-size:11px;color:#9ca3af;text-align:center}
        @media print{@page{margin:18mm}}
      </style></head><body>
      <h1>📊 DQ Profile Report</h1>
      <p class="sub">Model: <strong>${selectedModel?.name || sourceId}</strong> &nbsp;|&nbsp; Table: <strong>${tableName || "All tables"}</strong> &nbsp;|&nbsp; Generated: ${formatDateTime()}</p>
      <div class="stats">
        <div class="stat"><div class="stat-label">DQ Score</div><div class="stat-value" style="color:${scoreColor}">${score}</div></div>
        <div class="stat"><div class="stat-label">Total Rows</div><div class="stat-value">${profile.totalRows?.toLocaleString() ?? "—"}</div></div>
        <div class="stat"><div class="stat-label">Columns</div><div class="stat-value">${profile.totalColumns}</div></div>
        <div class="stat"><div class="stat-label">Anomalies</div><div class="stat-value" style="color:${anomalyCols.length > 0 ? '#dc2626' : '#16a34a'}">${anomalyCols.length}</div></div>
        <div class="stat"><div class="stat-label">Drift Detected</div><div class="stat-value" style="color:${driftCols.length > 0 ? '#d97706' : '#16a34a'}">${driftCols.length}</div></div>
      </div>
      <h2>Column Profiles</h2>
      <table><thead><tr>
        <th>Column</th><th>Type</th><th>Null %</th><th>Unique %</th><th>Mean</th><th>Std Dev</th><th>CV%</th><th>Anomalies</th><th>Drift</th>
      </tr></thead><tbody>
        ${profile.columns.map(col => {
          const flags = col.anomalyFlags ? Object.keys(col.anomalyFlags).filter(k => col.anomalyFlags[k]) : [];
          const sc = (col.nullRate ?? 0) > 0.3 ? 'color:#dc2626' : '';
          return `<tr>
            <td><strong>${col.name}</strong></td><td style="color:#6b7280">${col.type}</td>
            <td style="${sc}">${col.nullRate != null ? (col.nullRate*100).toFixed(1)+'%' : '—'}</td>
            <td>${col.uniquenessRate != null ? (col.uniquenessRate*100).toFixed(1)+'%' : '—'}</td>
            <td>${col.mean?.toFixed(3) ?? '—'}</td><td>${col.stdDev?.toFixed(3) ?? '—'}</td>
            <td>${col.cvPct != null ? col.cvPct.toFixed(1)+'%' : '—'}</td>
            <td>${flags.map(f => `<span class="badge">${f.replace(/_/g,' ')}</span>`).join('') || '—'}</td>
            <td>${col.driftLevel ?? '—'}</td>
          </tr>`;
        }).join('')}
      </tbody></table>
      <footer>Generated by DQ Studio &bull; ${formatDateTime()}</footer>
      <script>window.onload=()=>{window.print();}<\/script>
      </body></html>
    `);
    win.document.close();
  }, [profile, selectedModel, sourceId, tableName, anomalyCols, driftCols]);

  const headerActions = (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Data Model selector */}
      <div className="flex flex-col gap-0.5">
        <label className="text-[10px] text-gray-400 uppercase tracking-widest pl-1">Data Model</label>
        <Select
          value={sourceId}
          onValueChange={handleModelSelect}
          disabled={modelsLoading}
        >
          <SelectTrigger className="w-64 h-9 text-sm bg-white dark:bg-slate-900">
            <SelectValue placeholder={modelsLoading ? "Loading models…" : "Select a Data Model"} />
          </SelectTrigger>
          <SelectContent>
            {dataModels.map(m => (
              <SelectItem key={m.id} value={m.id}>
                <div className="flex items-center gap-2">
                  <Database className="w-3.5 h-3.5 text-blue-500" />
                  <span>{m.name}</span>
                </div>
              </SelectItem>
            ))}
            {dataModels.length === 0 && !modelsLoading && (
              <div className="px-4 py-3 text-sm text-gray-400">No data models found</div>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Table selector (shown when model has tables) */}
      {availableTables.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-gray-400 uppercase tracking-widest pl-1">Table (optional)</label>
          <Select value={tableName || "__all__"} onValueChange={v => setTableName(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-48 h-9 text-sm bg-white dark:bg-slate-900">
              <SelectValue placeholder="All tables" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All tables</SelectItem>
              {availableTables.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Fallback: manual table name if no tables in model definition */}
      {availableTables.length === 0 && sourceId && (
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] text-gray-400 uppercase tracking-widest pl-1">Table (optional)</label>
          <Input
            placeholder="e.g. sales_data"
            value={tableName}
            onChange={e => setTableName(e.target.value)}
            className="w-48 h-9 text-sm bg-white dark:bg-slate-900"
          />
        </div>
      )}

      <div className="flex flex-col gap-0.5 mt-auto">
        <label className="text-[10px] text-transparent pl-1">_</label>
        <div className="flex items-center gap-2">
          <CommonButton
            id="dq-run-profile-btn"
            onClick={runProfile}
            disabled={!sourceId || loading}
            sx={{ height: 36 }}
          >
            {loading
              ? <><span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Profiling…</>
              : "▶ Run Profile"}
          </CommonButton>
          {profile && (
            <>
              <button
                id="dq-export-csv-btn"
                onClick={exportCSV}
                title="Export as CSV"
                className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 hover:border-green-400 hover:text-green-600 transition-colors text-sm font-medium"
              >
                <Download className="w-3.5 h-3.5" />
                CSV
              </button>
              <button
                id="dq-export-pdf-btn"
                onClick={exportPDF}
                title="Export as PDF"
                className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-300 hover:border-red-400 hover:text-red-600 transition-colors text-sm font-medium"
              >
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <StandardPageLayout
      title="Data Profiler"
      description="Profile any data source — detect anomalies, track drift, explore column statistics."
      headerActions={headerActions}
    >
      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <LoadingSpinner />
          <p className="text-gray-500 dark:text-slate-400 text-sm">Profiling data source…</p>
        </div>
      )}

      {/* Summary cards + content */}
      {!loading && profile && (
        <>
          {/* Summary row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <HealthRing score={Math.round(profile.dqScore ?? 0)} />
            <StatCard label="Total Rows"     value={profile.totalRows?.toLocaleString()} icon={Activity}       color="text-blue-600"   bg="bg-blue-50 dark:bg-blue-900/20" />
            <StatCard label="Total Columns"  value={profile.totalColumns}                icon={Search}         color="text-violet-600" bg="bg-violet-50 dark:bg-violet-900/20" />
            <StatCard label="Anomalies"      value={anomalyCols.length}                  icon={AlertTriangle}  color={anomalyCols.length > 0 ? "text-red-600" : "text-green-600"} bg={anomalyCols.length > 0 ? "bg-red-50 dark:bg-red-900/20" : "bg-green-50 dark:bg-green-900/20"} />
            <StatCard label="Drift Detected" value={driftCols.length}                    icon={TrendingUp}     color={driftCols.length > 0 ? "text-yellow-600" : "text-green-600"} bg={driftCols.length > 0 ? "bg-yellow-50 dark:bg-yellow-900/20" : "bg-green-50 dark:bg-green-900/20"} />
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-slate-700 mb-4 flex gap-0">
            {[["columns", `Columns (${profile.totalColumns})`], ["anomalies", `Anomalies (${anomalyCols.length})`], ["history", `History (${history.length})`]].map(([key, label]) => (
              <button key={key} onClick={() => setActiveTab(key)}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === key ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400" : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Columns tab */}
          {activeTab === "columns" && (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-700">
                    {["Column", "Type", "Null %", "Unique %", "Mean", "Std Dev", "CV%", "Anomalies", "Drift"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {profile.columns.map((col, i) => (
                    <tr key={col.name} id={`dq-col-row-${i}`} onClick={() => setSelectedCol(col)}
                      className="cursor-pointer hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-slate-100">{col.name}</td>
                      <td className="px-4 py-3 text-gray-400 dark:text-slate-500 text-xs">{col.type}</td>
                      <td className="px-4 py-3">
                        <span className={(col.nullRate ?? 0) > 0.3 ? "text-red-600 dark:text-red-400 font-semibold" : "text-gray-600 dark:text-slate-300"}>
                          {col.nullRate != null ? `${(col.nullRate * 100).toFixed(1)}%` : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-slate-300">{col.uniquenessRate != null ? `${(col.uniquenessRate * 100).toFixed(1)}%` : "—"}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-slate-300">{col.mean?.toFixed(2) ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-slate-300">{col.stdDev?.toFixed(2) ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-slate-300">{col.cvPct != null ? `${col.cvPct.toFixed(1)}%` : "—"}</td>
                      <td className="px-4 py-3"><AnomalyBadge flags={col.anomalyFlags} /></td>
                      <td className="px-4 py-3"><DriftBadge level={col.driftLevel} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Anomalies tab */}
          {activeTab === "anomalies" && (
            anomalyCols.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">No Anomalies Detected</h3>
                <p className="text-gray-500 dark:text-slate-400 text-sm">All columns are within normal thresholds.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {anomalyCols.map(col => (
                  <div key={col.name} onClick={() => { setSelectedCol(col); setActiveTab("columns"); }}
                    className="group bg-white dark:bg-slate-900 rounded-xl p-4 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-slate-100 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">{col.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{col.type}</p>
                      </div>
                    </div>
                    <AnomalyBadge flags={col.anomalyFlags} />
                    <div className="mt-3 flex gap-3 text-xs text-gray-400">
                      {col.nullRate != null && <span>Null: {(col.nullRate * 100).toFixed(1)}%</span>}
                      {col.zeroRate != null && <span>Zero: {(col.zeroRate * 100).toFixed(1)}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* History tab */}
          {activeTab === "history" && (
            history.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-slate-400">No previous profile runs found for this source.</p>
              </div>
            ) : (
              <>
                {/* ── DQ Score Trend Chart ── */}
                {history.length >= 2 && (
                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm p-5 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">DQ Score Trend</p>
                        <p className="text-xs text-gray-400 mt-0.5">Score over the last {history.length} profile runs</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 inline-block rounded" /> DQ Score</span>
                        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-400 inline-block rounded border-dashed border-t border-emerald-400" /> Target (80)</span>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart
                        data={[...history].reverse().map((r, i) => ({
                          run: `Run ${i + 1}`,
                          date: r.startedAt ? formatDateTime(r.startedAt) : `#${i+1}`,
                          score: r.dqScore != null ? parseFloat(r.dqScore.toFixed(1)) : null,
                          rows: r.totalRows ?? 0,
                        }))}
                        margin={{ top: 5, right: 16, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" className="dark:stroke-slate-700" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#9ca3af" }} unit="" />
                        <Tooltip
                          contentStyle={{ borderRadius: 10, fontSize: 12, border: "1px solid #e5e7eb" }}
                          formatter={(v, name) => [name === "score" ? `${v}` : v.toLocaleString(), name === "score" ? "DQ Score" : "Rows"]}
                          labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                        />
                        {/* Target line at 80 */}
                        <Line type="monotone" dataKey={() => 80} stroke="#34d399" strokeWidth={1.5} strokeDasharray="5 4" dot={false} legendType="none" />
                        <Line
                          type="monotone"
                          dataKey="score"
                          stroke="#3b82f6"
                          strokeWidth={2.5}
                          dot={{ fill: "#3b82f6", r: 4, strokeWidth: 0 }}
                          activeDot={{ r: 6, fill: "#2563eb" }}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* ── History table ── */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-700">
                        {["Run ID", "Table", "DQ Score", "Rows", "Columns", "Status", "Run At"].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((run) => (
                        <tr key={run.executionId} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800">
                          <td className="px-4 py-3 font-mono text-xs text-gray-400">{run.executionId?.slice(0, 8)}…</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-slate-300">{run.tableName ?? "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`font-semibold ${(run.dqScore ?? 0) >= 80 ? "text-green-600" : (run.dqScore ?? 0) >= 60 ? "text-yellow-600" : "text-red-600"}`}>
                              {run.dqScore?.toFixed(1) ?? "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-slate-300">{run.totalRows?.toLocaleString() ?? "—"}</td>
                          <td className="px-4 py-3 text-gray-600 dark:text-slate-300">{run.totalColumns ?? "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${run.status === "Completed" ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300" : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300"}`}>
                              {run.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-xs">{run.startedAt ? formatDateTime(run.startedAt) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )
          )}
        </>
      )}

      {/* Empty state — rich landing */}
      {!loading && !profile && !error && (
        <div className="py-10">
          {/* Hero */}
          <div className="text-center mb-10">
            <div className="relative inline-flex items-center justify-center w-20 h-20 mb-5">
              <div className="absolute inset-0 rounded-full bg-blue-100 dark:bg-blue-900/30 animate-pulse" />
              <div className="relative bg-gradient-to-br from-blue-500 to-violet-600 rounded-full w-16 h-16 flex items-center justify-center shadow-lg shadow-blue-200 dark:shadow-blue-900/40">
                <ShieldCheck className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">Start Profiling Your Data</h2>
            <p className="text-gray-500 dark:text-slate-400 max-w-lg mx-auto text-sm leading-relaxed">
              Select a <strong className="text-gray-700 dark:text-slate-200">Data Model</strong> from the dropdown above, choose a table, and click <strong className="text-gray-700 dark:text-slate-200">▶ Run Profile</strong> to instantly analyse your data quality.
            </p>
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              {
                icon: Search,
                color: "text-blue-600",
                bg: "bg-blue-50 dark:bg-blue-900/20",
                border: "border-blue-100 dark:border-blue-900/40",
                title: "Column Statistics",
                desc: "Null rates, unique counts, mean, std dev, CV% and distribution histograms for every column.",
              },
              {
                icon: AlertTriangle,
                color: "text-red-500",
                bg: "bg-red-50 dark:bg-red-900/20",
                border: "border-red-100 dark:border-red-900/40",
                title: "Anomaly Detection",
                desc: "Automatically flags HIGH_NULL, HIGH_CARDINALITY, HIGH_OUTLIER_RATE and more.",
              },
              {
                icon: TrendingUp,
                color: "text-amber-500",
                bg: "bg-amber-50 dark:bg-amber-900/20",
                border: "border-amber-100 dark:border-amber-900/40",
                title: "Drift Tracking",
                desc: "Compares each run against the previous baseline and scores drift as Low / Medium / High.",
              },
              {
                icon: Activity,
                color: "text-violet-600",
                bg: "bg-violet-50 dark:bg-violet-900/20",
                border: "border-violet-100 dark:border-violet-900/40",
                title: "DQ Score & History",
                desc: "A composite 0–100 quality score saved on every run with a full trend chart over time.",
              },
            ].map(({ icon: Icon, color, bg, border, title, desc }) => (
              <div key={title} className={`rounded-xl p-5 border ${border} bg-white dark:bg-slate-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all`}>
                <div className={`${bg} w-10 h-10 rounded-xl flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <p className="font-semibold text-gray-900 dark:text-slate-100 text-sm mb-1">{title}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* How it works */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-800 shadow-sm p-6">
            <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-5">How it works</p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {[
                { step: "1", label: "Select Model", detail: "Pick a Data Model from the dropdown" },
                { step: "2", label: "Choose Table", detail: "Optionally filter to a specific table" },
                { step: "3", label: "Run Profile", detail: "Click ▶ Run Profile to start analysis" },
                { step: "4", label: "Export Results", detail: "Download as CSV or PDF report" },
              ].map(({ step, label, detail }, i, arr) => (
                <div key={step} className="flex items-center gap-4 flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shadow-md shadow-blue-200 dark:shadow-blue-900/40 mb-2">
                      {step}
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{label}</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500 text-center mt-0.5">{detail}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="hidden sm:block text-gray-200 dark:text-slate-700 text-2xl font-light pb-6">›</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Column detail panel */}
      {selectedCol && <ColumnDetailPanel col={selectedCol} onClose={() => setSelectedCol(null)} />}
    </StandardPageLayout>
  );
}
